import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { MultiSelect } from "primereact/multiselect";
import { AutoComplete, AutoCompleteCompleteEvent } from "primereact/autocomplete";
import { Button } from "primereact/button";
import { Tag } from "primereact/tag";
import { Checkbox } from "primereact/checkbox";
import { Dialog } from "primereact/dialog";
import { ProgressBar } from "primereact/progressbar";
import PageContainer from "../../Components/Page/PageContainer";
import MultiSearch from "../../Components/SearchInputs/MultiSearch/MultiSearch";
import { ROUTES } from "../../config/routes";
import { trackToolAction } from "../../utils/analytics";
import {
  channelCheckupApi,
  channelManagerLabels,
  ChannelCheckupReport,
  ChannelCheckupSyncStatus,
  emptySyncStatus,
  keepPublicRecentUploads,
} from "../../api/channelCheckupApi";
import "./ChannelCheckup.css";

type RecentUpload = ChannelCheckupReport["recentUploads"][number];

type ThumbnailPreviewState = {
  uploads: RecentUpload[];
  index: number;
};

function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function dayDiffFromToday(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const target = new Date(iso);
  if (Number.isNaN(target.getTime())) return null;
  const msPerDay = 24 * 60 * 60 * 1000;
  const startOfDay = (d: Date) =>
    new Date(d.getFullYear(), d.getMonth(), d.getDate());
  return Math.round(
    (startOfDay(target).getTime() - startOfDay(new Date()).getTime()) / msPerDay
  );
}

/** e.g. "3 days ahead" / "5 days ago" / "today". */
function relativeDaysLabel(iso: string | null | undefined): string | null {
  const diff = dayDiffFromToday(iso);
  if (diff === null) return null;
  if (diff === 0) return "today";
  const n = Math.abs(diff);
  const unit = n === 1 ? "day" : "days";
  return diff > 0 ? `${n} ${unit} ahead` : `${n} ${unit} ago`;
}

/** A date plus a muted relative label, or a "not tracked" note. */
const DateCell: React.FC<{
  iso: string | null;
  tracked: boolean;
}> = ({ iso, tracked }) => {
  if (!tracked) {
    return <span className="text-color-secondary text-sm">not tracked</span>;
  }
  const rel = relativeDaysLabel(iso);
  return (
    <div>
      <div>{formatDate(iso)}</div>
      {rel && <div className="text-sm text-color-secondary">{rel}</div>}
    </div>
  );
};

/** A date plus a muted relative label, with no "not tracked" state (nulls are genuine). */
const RelativeDateCell: React.FC<{ iso: string | null }> = ({ iso }) => {
  const rel = relativeDaysLabel(iso);
  return (
    <div>
      <div>{formatDate(iso)}</div>
      {rel && <div className="text-sm text-color-secondary">{rel}</div>}
    </div>
  );
};

/** True when `iso` is a valid time strictly after now. */
function isFuture(iso: string | null | undefined): boolean {
  if (!iso) return false;
  const t = new Date(iso).getTime();
  return !Number.isNaN(t) && t > Date.now();
}

/**
 * Scheduled date with "last published N days ago" fallback (prototype behaviour).
 * A schedule that has gone live since the last sync (the page can stay open
 * for hours, and syncs run every 12h) is treated as nothing scheduled.
 */
const ScheduledDateCell: React.FC<{
  scheduled: string | null;
  fallbackPublished: string | null;
}> = ({ scheduled, fallbackPublished }) => {
  if (scheduled && isFuture(scheduled)) {
    return <RelativeDateCell iso={scheduled} />;
  }
  const rel = relativeDaysLabel(fallbackPublished);
  if (fallbackPublished && rel) {
    return (
      <div>
        <div className="text-color-secondary">—</div>
        <div className="text-sm text-color-secondary">last published {rel}</div>
      </div>
    );
  }
  return <span className="text-color-secondary">—</span>;
};

/** e.g. "2 hours ago" / "3 days ago" / "just now" for a sync timestamp. */
function relativeTimeLabel(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  const diffMs = Date.now() - t;
  const minutes = Math.round(diffMs / 60000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `${hours} ${hours === 1 ? "hour" : "hours"} ago`;
  const days = Math.round(hours / 24);
  return `${days} days ago`;
}

/** Extras sync is twice daily; anything older than this is overdue. */
const EXTRAS_STALE_AFTER_MS = 18 * 60 * 60 * 1000;
/** Channel settings (managers) sync daily; flag after a missed run. */
const CHANNEL_SETTINGS_STALE_AFTER_MS = 30 * 60 * 60 * 1000;
/**
 * A per-channel value that hasn't refreshed for this long has missed at
 * least two twice-daily runs — its lookup is failing and the shown value is
 * being kept from an earlier sync.
 */
const VALUE_STALE_AFTER_MS = 30 * 60 * 60 * 1000;

function isStale(iso: string | null | undefined, maxAgeMs: number): boolean {
  if (!iso) return true;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return true;
  return Date.now() - t > maxAgeMs;
}

/**
 * Warning icon for a single per-channel value whose own lookup has stopped
 * refreshing (the sync keeps the last good value on failure). `syncedAt`
 * null means "never refreshed since per-value tracking began" — only flagged
 * once tracking has started (`trackingStarted`), so the first deploy doesn't
 * light up every row.
 */
const StaleValueMarker: React.FC<{
  syncedAt: string | null;
  trackingStarted: boolean;
}> = ({ syncedAt, trackingStarted }) => {
  const stale = syncedAt
    ? isStale(syncedAt, VALUE_STALE_AFTER_MS)
    : trackingStarted;
  if (!stale) return null;
  const since = syncedAt
    ? `since ${new Date(syncedAt).toLocaleString()}`
    : "since tracking began";
  return (
    <i
      className="pi pi-exclamation-triangle text-orange-500"
      style={{ marginLeft: "0.25rem", fontSize: "0.8rem" }}
      title={`Not refreshed ${since} — the lookup for this channel is failing, so this value is from an earlier sync.`}
    />
  );
};

/**
 * One line per data source: when its snapshot was last written, with the
 * sync's recorded warnings expandable underneath. Makes a failed ClickHouse
 * day or a skipped channel visible instead of looking like bad channel data.
 */
const SyncStatusBar: React.FC<{ status: ChannelCheckupSyncStatus }> = ({
  status,
}) => {
  const [showWarnings, setShowWarnings] = useState(false);
  const sources: Array<{
    label: string;
    updatedAt: string | null;
    warnings: string[];
    staleAfterMs: number | null;
  }> = [
    {
      label: "Dates (featured / scheduled / published)",
      updatedAt: status.extras.updatedAt,
      warnings: status.extras.warnings,
      staleAfterMs: EXTRAS_STALE_AFTER_MS,
    },
    {
      label: "Recent uploads",
      updatedAt: status.recentUploads.updatedAt,
      warnings: status.recentUploads.warnings,
      staleAfterMs: EXTRAS_STALE_AFTER_MS,
    },
    {
      label: "Playlists",
      updatedAt: status.playlists.updatedAt,
      warnings: status.playlists.warnings,
      staleAfterMs: null,
    },
    {
      label: "Channel managers",
      updatedAt: status.channelSettings.updatedAt,
      warnings: status.channelSettings.warnings,
      staleAfterMs: CHANNEL_SETTINGS_STALE_AFTER_MS,
    },
  ];
  const allWarnings = sources.flatMap((s) =>
    s.warnings.map((w) => `${s.label}: ${w}`)
  );
  return (
    <div className="flex flex-column gap-1">
      <div className="flex flex-wrap gap-3 text-sm text-color-secondary align-items-center">
        {sources.map((s) => {
          const rel = relativeTimeLabel(s.updatedAt);
          const stale =
            s.staleAfterMs !== null && isStale(s.updatedAt, s.staleAfterMs);
          return (
            <span
              key={s.label}
              title={s.updatedAt ? new Date(s.updatedAt).toLocaleString() : ""}
            >
              {s.label} synced{" "}
              <span className={stale ? "text-orange-500" : undefined}>
                {rel ?? "never"}
              </span>
              {stale && (
                <i
                  className="pi pi-exclamation-triangle text-orange-500"
                  style={{ marginLeft: "0.25rem", fontSize: "0.8rem" }}
                  title="Sync is overdue — dates may be stale"
                />
              )}
            </span>
          );
        })}
        {allWarnings.length > 0 && (
          <Button
            type="button"
            link
            size="small"
            icon={showWarnings ? "pi pi-chevron-up" : "pi pi-chevron-down"}
            label={`${allWarnings.length} sync ${
              allWarnings.length === 1 ? "warning" : "warnings"
            }`}
            onClick={() => setShowWarnings((v) => !v)}
            style={{ padding: 0 }}
            className="text-sm"
          />
        )}
      </div>
      {showWarnings && allWarnings.length > 0 && (
        <ul
          className="text-sm text-color-secondary"
          style={{ margin: 0, paddingLeft: "1.25rem" }}
        >
          {allWarnings.map((w) => (
            <li key={w}>{w}</li>
          ))}
        </ul>
      )}
    </div>
  );
};

/** Unique, sorted {label, value} options for a MultiSelect filter. */
function uniqueOptions(values: string[]): { label: string; value: string }[] {
  const set = new Set(values.map((v) => v.trim()).filter(Boolean));
  return Array.from(set)
    .sort((a, b) => a.localeCompare(b))
    .map((v) => ({ label: v, value: v }));
}

/** hqdefault (~480×360) for table spot-checking; mqdefault is too small when enlarged. */
function recentUploadThumbnailUrl(videoId: string): string {
  return `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`;
}

/** Larger still for dialog inspection (falls back visually if maxres missing). */
function recentUploadPreviewThumbnailUrl(videoId: string): string {
  return `https://i.ytimg.com/vi/${videoId}/maxresdefault.jpg`;
}

const RECENT_UPLOAD_THUMB_WIDTH = 320;
const RECENT_UPLOAD_THUMB_HEIGHT = 180;
const TABLE_TEXT_MAX_CHARS = 140;

function collapseWhitespace(value: string): string {
  return value.replace(/\s+/g, " ").trim();
}

function truncateText(
  value: string | null | undefined,
  max = TABLE_TEXT_MAX_CHARS
): string {
  if (!value) return "—";
  const collapsed = collapseWhitespace(value);
  if (!collapsed) return "—";
  if (collapsed.length <= max) return collapsed;
  return `${collapsed.slice(0, max).trimEnd()}…`;
}

function formatTags(tags: string[] | undefined): string {
  if (!tags || tags.length === 0) return "—";
  return tags.join(", ");
}

function privacyTagSeverity(
  status: string | undefined
): "success" | "warning" | "info" | "secondary" {
  const s = (status || "").toLowerCase();
  if (s === "public") return "success";
  if (s === "unlisted") return "info";
  if (s === "private") return "warning";
  return "secondary";
}

const DialogPanel: React.FC<{
  label: string;
  children: React.ReactNode;
  variant?: "card" | "ground";
}> = ({ label, children, variant = "ground" }) => (
  <div
    className={`channel-checkup-dialog-panel channel-checkup-dialog-panel--${variant}`}
  >
    <div className="channel-checkup-thumb-dialog-label">{label}</div>
    {children}
  </div>
);

const DialogTagChips: React.FC<{ tags?: string[] }> = ({ tags }) => {
  if (!tags || tags.length === 0) {
    return <span>—</span>;
  }
  return (
    <div className="channel-checkup-dialog-chips">
      {tags.map((tag, i) => (
        <Tag key={`${tag}-${i}`} value={tag} />
      ))}
    </div>
  );
};

/** True when the channel is paused/inactive and should be hidden by default. */
function isPausedOrInactive(r: ChannelCheckupReport): boolean {
  if (r.inactive) return true;
  const op = (r.operationStatus || "").toLowerCase();
  return op === "paused" || op === "inactive";
}

const BANNER_STATE_OPTIONS = [
  { label: "Default", value: "default" },
  { label: "No default", value: "no_default" },
  { label: "Brand boost", value: "brand_boost" },
  { label: "External change", value: "external_change" },
];

interface ChannelCheckupFilters {
  channelType: string[];
  brand: string[];
  bannerState: string[];
  channelManagers: string[];
}

const defaultFilters = (): ChannelCheckupFilters => ({
  channelType: [],
  brand: [],
  bannerState: [],
  channelManagers: [],
});

type ColumnId =
  | "banner"
  | "type"
  | "brand"
  | "channelManagers"
  | "featuredVideo"
  | "scheduledVideo"
  | "scheduledShort"
  | "lastPublished"
  | "lastUpload"
  | "playlists"
  | "details"
  | "qc";

const FEATURED_VIDEO_TOOLTIP =
  "Publish date of the channel trailer shown to non-subscribers. The date the featured video was changed isn't available from the YouTube API, and the 'featured video for returning subscribers' isn't exposed by the API at all.";

/** Where the schedule comes from and how fresh it is. */
const SCHEDULE_LAG_NOTE =
  "Every scheduled video the data warehouse knows about is re-checked on YouTube at each sync (06:00 / 18:00 UTC), so moved or cancelled schedules are picked up. Newly uploaded videos reach the warehouse overnight, so a video scheduled today appears from the next morning.";

const PLAYLIST_LAST_ADDED_TOOLTIP =
  "When a video was most recently ADDED to this playlist. Adding an old video counts — this is not the video's publish date.";
const PLAYLIST_NEWEST_VIDEO_TOOLTIP =
  "Publish date of the newest video in this playlist, regardless of when it was added.";

const COLUMN_OPTIONS: { id: ColumnId; label: string }[] = [
  { id: "banner", label: "Current Banner" },
  { id: "type", label: "Type" },
  { id: "brand", label: "Brand" },
  { id: "channelManagers", label: "Channel Manager" },
  { id: "featuredVideo", label: "Featured Video Published" },
  { id: "scheduledVideo", label: "Farthest Scheduled Video" },
  { id: "scheduledShort", label: "Farthest Scheduled Short" },
  { id: "lastPublished", label: "Last Published" },
  { id: "lastUpload", label: "Last Upload" },
  { id: "playlists", label: "Playlists" },
  { id: "details", label: "Details" },
  { id: "qc", label: "QC" },
];

/** Playlists count then Details (expand buttons) then QC; Last Upload hidden by default. */
const DEFAULT_VISIBLE_COLUMNS: ColumnId[] = [
  "banner",
  "type",
  "brand",
  "channelManagers",
  "featuredVideo",
  "scheduledVideo",
  "scheduledShort",
  "lastPublished",
  "playlists",
  "details",
  "qc",
];

/**
 * Filters, column visibility, and channel selection are persisted across
 * visits (localStorage — the codebase has no cookie usage/dependency
 * anywhere; useTheme.ts already establishes localStorage as the convention
 * for this kind of UI preference).
 */
const PREFS_STORAGE_KEY = "channelCheckup:prefs:v5";

type ExpansionPanel = "playlists" | "uploads";

function loadPersistedPrefs(): {
  filters?: ChannelCheckupFilters;
  visibleColumns?: ColumnId[];
  selectedChannelIds?: string[];
  showPausedInactive?: boolean;
} {
  try {
    const raw = localStorage.getItem(PREFS_STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

type ChannelSearchOption = {
  channelId: string;
  label: string;
};

const ChannelCheckup: React.FC = () => {
  const navigate = useNavigate();

  const persistedPrefs = useMemo(() => loadPersistedPrefs(), []);

  const [allReports, setAllReports] = useState<ChannelCheckupReport[]>([]);
  // Per-value freshness only means something once a sync has written it.
  const featuredTrackingStarted = useMemo(
    () => allReports.some((r) => r.featuredVideoSyncedAt),
    [allReports]
  );
  const videoDatesTrackingStarted = useMemo(
    () => allReports.some((r) => r.videoDatesSyncedAt),
    [allReports]
  );
  const [syncStatus, setSyncStatus] = useState<ChannelCheckupSyncStatus>(
    emptySyncStatus()
  );
  const [loading, setLoading] = useState(true);
  const [selectedChannelIds, setSelectedChannelIds] = useState<string[]>(
    persistedPrefs.selectedChannelIds ?? []
  );
  const [filters, setFilters] = useState<ChannelCheckupFilters>(
    persistedPrefs.filters ?? defaultFilters()
  );
  const [showPausedInactive, setShowPausedInactive] = useState(
    persistedPrefs.showPausedInactive ?? false
  );
  const [expandedRows, setExpandedRows] = useState<
    ChannelCheckupReport[] | undefined
  >(undefined);
  const [expandedPanel, setExpandedPanel] = useState<ExpansionPanel | null>(
    null
  );
  const [thumbnailPreview, setThumbnailPreview] =
    useState<ThumbnailPreviewState | null>(null);
  const [channelPreviewId, setChannelPreviewId] = useState<string | null>(null);
  const [visibleColumns, setVisibleColumns] = useState<ColumnId[]>(
    persistedPrefs.visibleColumns ?? DEFAULT_VISIBLE_COLUMNS
  );
  const [channelSearchSuggestions, setChannelSearchSuggestions] = useState<
    ChannelSearchOption[]
  >([]);
  const [channelSearchQuery, setChannelSearchQuery] = useState("");

  // Persist filters + channel selection + column visibility so a returning
  // user sees the same view.
  useEffect(() => {
    try {
      localStorage.setItem(
        PREFS_STORAGE_KEY,
        JSON.stringify({
          filters,
          visibleColumns,
          selectedChannelIds,
          showPausedInactive,
        })
      );
    } catch {
      // ignore storage failures (e.g. private-browsing quota) — not critical
    }
  }, [filters, visibleColumns, selectedChannelIds, showPausedInactive]);

  useEffect(() => {
    trackToolAction("ChannelCheckup", "load");
    channelCheckupApi
      .listChannels()
      .then(({ reports, syncStatus: status }) => {
        setSyncStatus(status);
        setAllReports(
          reports.map((r) => {
            const recentUploads = keepPublicRecentUploads(
              r.recentUploads ?? []
            );
            return {
              ...r,
              recentUploads,
              recentUploadCount: recentUploads.length,
            };
          })
        );
      })
      .finally(() => setLoading(false));
  }, []);

  const channelSearchOptions = useMemo<ChannelSearchOption[]>(
    () =>
      allReports.map((r) => ({
        channelId: r.channelId,
        label: `${r.channelTitle || r.channelId} (${r.channelId})`,
      })),
    [allReports]
  );

  // Options for the MultiSelect filters, derived from the loaded data.
  const typeOptions = useMemo(
    () =>
      uniqueOptions(
        allReports.map((r) => r.channelType).filter(Boolean) as string[]
      ),
    [allReports]
  );
  const brandOptions = useMemo(
    () =>
      uniqueOptions(
        allReports.flatMap((r) => (r.brand ? r.brand.split(",") : []))
      ),
    [allReports]
  );
  const channelManagerOptions = useMemo(
    () => uniqueOptions(allReports.flatMap((r) => channelManagerLabels(r))),
    [allReports]
  );

  // Rows shown in the table — computed manually (not PrimeReact's built-in
  // filter engine) so the "showing X of Y" count always reflects exactly
  // what's on screen, including the channel-ID selection.
  const rows = useMemo(() => {
    let result = allReports;

    if (!showPausedInactive) {
      result = result.filter((r) => !isPausedOrInactive(r));
    }
    if (selectedChannelIds.length > 0) {
      const set = new Set(selectedChannelIds);
      result = result.filter((r) => set.has(r.channelId));
    }
    if (filters.channelType.length > 0) {
      const set = new Set(filters.channelType);
      result = result.filter((r) => r.channelType && set.has(r.channelType));
    }
    if (filters.brand.length > 0) {
      const set = new Set(filters.brand.map((v) => v.toLowerCase()));
      result = result.filter((r) =>
        (r.brand || "")
          .split(",")
          .map((b) => b.trim().toLowerCase())
          .some((b) => set.has(b))
      );
    }
    if (filters.bannerState.length > 0) {
      const set = new Set(filters.bannerState);
      result = result.filter((r) => r.bannerState && set.has(r.bannerState));
    }
    if (filters.channelManagers.length > 0) {
      const set = new Set(filters.channelManagers.map((v) => v.toLowerCase()));
      result = result.filter((r) =>
        channelManagerLabels(r).some((label) => set.has(label.toLowerCase()))
      );
    }

    return result;
  }, [allReports, selectedChannelIds, filters, showPausedInactive]);

  const hiddenPausedCount = useMemo(
    () =>
      showPausedInactive
        ? 0
        : allReports.filter((r) => isPausedOrInactive(r)).length,
    [allReports, showPausedInactive]
  );

  const resetFilters = () => {
    setFilters(defaultFilters());
    setSelectedChannelIds([]);
    setChannelSearchQuery("");
    setShowPausedInactive(false);
  };

  const isColumnVisible = (id: ColumnId) => visibleColumns.includes(id);

  const searchChannels = (event: AutoCompleteCompleteEvent) => {
    const q = (event.query || "").trim().toLowerCase();
    if (!q) {
      setChannelSearchSuggestions(channelSearchOptions.slice(0, 25));
      return;
    }
    setChannelSearchSuggestions(
      channelSearchOptions
        .filter(
          (opt) =>
            opt.label.toLowerCase().includes(q) ||
            opt.channelId.toLowerCase().includes(q)
        )
        .slice(0, 25)
    );
  };

  const onChannelSearchSelect = (option: ChannelSearchOption) => {
    setSelectedChannelIds((prev) =>
      prev.includes(option.channelId) ? prev : [...prev, option.channelId]
    );
    setChannelSearchQuery("");
  };

  // ----- Column body templates -----

  const channelBody = (r: ChannelCheckupReport) => (
    <div className="flex flex-column gap-1">
      <a
        href={r.channelUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="font-medium"
      >
        {r.channelTitle || r.channelId}
      </a>
      <span
        className="text-sm text-color-secondary"
        style={{ fontFamily: "monospace" }}
      >
        {r.channelId}
      </span>
      {isPausedOrInactive(r) && (
        <Tag
          value={r.operationStatus || (r.inactive ? "Inactive" : "Paused")}
          severity="warning"
        />
      )}
    </div>
  );

  const bannerBody = (r: ChannelCheckupReport) => {
    return (
      <button
        type="button"
        className="channel-checkup-thumb-button"
        aria-label={`Inspect channel details for ${r.channelTitle || r.channelId}`}
        onClick={(e) => {
          e.stopPropagation();
          setChannelPreviewId(r.channelId);
        }}
        style={{
          padding: 0,
          border: "none",
          background: "transparent",
          cursor: "pointer",
          display: "flex",
          flexDirection: "column",
          gap: 4,
          alignItems: "flex-start",
          textAlign: "left",
        }}
      >
        {r.bannerUrl ? (
          <img
            src={r.bannerUrl}
            alt=""
            style={{
              height: "150px",
              minWidth: "min(22vw, 380px)",
              maxWidth: "min(30vw, 480px)",
              objectFit: "cover",
              borderRadius: "4px",
              display: "block",
            }}
          />
        ) : (
          <span className="text-color-secondary">No banner — view details</span>
        )}
        {r.bannerState && (
          <Tag
            value={r.bannerState}
            severity={r.bannerState === "external_change" ? "warning" : "info"}
          />
        )}
      </button>
    );
  };

  const brandBody = (r: ChannelCheckupReport) => (
    <span
      style={{ whiteSpace: "normal", wordBreak: "break-word" }}
      title={r.brand || ""}
    >
      {r.brand || "—"}
    </span>
  );

  const channelManagerBody = (r: ChannelCheckupReport) => {
    const labels = channelManagerLabels(r);
    if (labels.length === 0) {
      return <span className="text-color-secondary">—</span>;
    }
    return (
      <div
        style={{ whiteSpace: "normal", wordBreak: "break-word" }}
        title={r.channelManagerEmails.join(", ")}
      >
        {labels.map((label) => (
          <div key={label}>{label}</div>
        ))}
      </div>
    );
  };

  const lastPublishedBody = (r: ChannelCheckupReport) => (
    <DateCell iso={r.lastPublishedAt} tracked={r.lastUpdatesAvailable} />
  );
  const lastUploadBody = (r: ChannelCheckupReport) => (
    <DateCell iso={r.lastUploadAt} tracked={r.lastUpdatesAvailable} />
  );
  const lastUploadHeader = (
    <span>
      Last Upload{" "}
      <i
        className="pi pi-info-circle text-color-secondary"
        title="When the video FILE was uploaded to YouTube's servers — distinct from Last Published, the moment it went (or goes) live/public."
      />
    </span>
  );
  const featuredVideoBody = (r: ChannelCheckupReport) => (
    <span>
      {formatDate(r.featuredVideoDate)}
      <StaleValueMarker
        syncedAt={r.featuredVideoSyncedAt}
        trackingStarted={featuredTrackingStarted}
      />
    </span>
  );
  const featuredVideoHeader = (
    <span>
      Featured Video Published{" "}
      <i
        className="pi pi-info-circle text-color-secondary"
        title={FEATURED_VIDEO_TOOLTIP}
      />
    </span>
  );
  const scheduledVideoBody = (r: ChannelCheckupReport) => (
    <div className="flex align-items-start">
      <ScheduledDateCell
        scheduled={r.maxScheduledVideo}
        fallbackPublished={r.lastPublishedAt}
      />
      <StaleValueMarker
        syncedAt={r.videoDatesSyncedAt}
        trackingStarted={videoDatesTrackingStarted}
      />
    </div>
  );
  const scheduledShortBody = (r: ChannelCheckupReport) => (
    <div className="flex align-items-start">
      <ScheduledDateCell
        scheduled={r.maxScheduledShort}
        fallbackPublished={r.lastPublishedAt}
      />
      <StaleValueMarker
        syncedAt={r.videoDatesSyncedAt}
        trackingStarted={videoDatesTrackingStarted}
      />
    </div>
  );
  const scheduledVideoHeader = (
    <span>
      Farthest Scheduled Video{" "}
      <i
        className="pi pi-info-circle text-color-secondary"
        title={`Farthest future scheduled VOD date. When nothing is scheduled, shows the channel’s last published date. ${SCHEDULE_LAG_NOTE}`}
      />
    </span>
  );
  const scheduledShortHeader = (
    <span>
      Farthest Scheduled Short{" "}
      <i
        className="pi pi-info-circle text-color-secondary"
        title={`Farthest future scheduled Short date. When nothing is scheduled, shows the channel’s last published date. ${SCHEDULE_LAG_NOTE}`}
      />
    </span>
  );

  const openTitleQc = (channelId: string) => {
    const params = new URLSearchParams({
      channelKeys: channelId,
      myIssues: "0",
    });
    navigate(`${ROUTES.METADATA_AUDIT_TITLE_QC}?${params.toString()}`);
  };

  const qcBody = (r: ChannelCheckupReport) => (
    <Button
      label={`QC (${r.qcOpenIssueCount ?? 0})`}
      icon="pi pi-external-link"
      link
      onClick={() => openTitleQc(r.channelId)}
      style={{ padding: 0 }}
    />
  );

  const allowRowExpansion = (r: ChannelCheckupReport) =>
    r.playlistCount > 0 || r.recentUploads.length > 0;

  const isRowPanelOpen = (r: ChannelCheckupReport, panel: ExpansionPanel) =>
    Boolean(expandedRows?.some((row) => row.channelId === r.channelId)) &&
    expandedPanel === panel;

  const toggleExpansionPanel = (
    r: ChannelCheckupReport,
    panel: ExpansionPanel
  ) => {
    const canOpen =
      panel === "playlists"
        ? r.playlistCount > 0
        : r.recentUploads.length > 0;
    if (!canOpen) return;

    if (isRowPanelOpen(r, panel)) {
      setExpandedRows(undefined);
      setExpandedPanel(null);
      return;
    }

    setExpandedPanel(panel);
    setExpandedRows([r]);
  };

  const playlistsBody = (r: ChannelCheckupReport) => (
    <span>{r.playlistCount > 0 ? r.playlistCount : "—"}</span>
  );

  const detailsBody = (r: ChannelCheckupReport) => (
    <div className="flex flex-column gap-1">
      <Button
        type="button"
        label="Playlists"
        icon={
          isRowPanelOpen(r, "playlists")
            ? "pi pi-chevron-down"
            : "pi pi-chevron-right"
        }
        size="small"
        outlined
        disabled={r.playlistCount === 0}
        onClick={() => toggleExpansionPanel(r, "playlists")}
        style={{ whiteSpace: "nowrap" }}
      />
      <Button
        type="button"
        label="Recent uploads"
        icon={
          isRowPanelOpen(r, "uploads")
            ? "pi pi-chevron-down"
            : "pi pi-chevron-right"
        }
        size="small"
        outlined
        disabled={r.recentUploads.length === 0}
        onClick={() => toggleExpansionPanel(r, "uploads")}
        style={{ whiteSpace: "nowrap" }}
      />
    </div>
  );

  const openThumbnailPreview = useCallback(
    (uploads: RecentUpload[], index: number) => {
      if (uploads.length === 0) return;
      setThumbnailPreview({ uploads, index });
    },
    []
  );

  const stepThumbnailPreview = useCallback((delta: number) => {
    setThumbnailPreview((prev) => {
      if (!prev || prev.uploads.length === 0) return prev;
      const len = prev.uploads.length;
      const nextIndex = (prev.index + delta + len) % len;
      return { ...prev, index: nextIndex };
    });
  }, []);

  const previewChannelIndex = useMemo(() => {
    if (!channelPreviewId) return -1;
    return rows.findIndex((r) => r.channelId === channelPreviewId);
  }, [channelPreviewId, rows]);

  const previewChannel = useMemo(() => {
    if (!channelPreviewId) return null;
    if (previewChannelIndex >= 0) return rows[previewChannelIndex];
    return allReports.find((r) => r.channelId === channelPreviewId) ?? null;
  }, [channelPreviewId, previewChannelIndex, rows, allReports]);

  const stepChannelPreview = useCallback(
    (delta: number) => {
      if (rows.length === 0) return;
      setChannelPreviewId((prev) => {
        if (!prev) return prev;
        const idx = rows.findIndex((r) => r.channelId === prev);
        const from = idx >= 0 ? idx : 0;
        const next = (from + delta + rows.length) % rows.length;
        return rows[next].channelId;
      });
    },
    [rows]
  );

  useEffect(() => {
    if (!thumbnailPreview && !previewChannel) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
      e.preventDefault();
      const delta = e.key === "ArrowLeft" ? -1 : 1;
      if (thumbnailPreview) stepThumbnailPreview(delta);
      else stepChannelPreview(delta);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [
    thumbnailPreview,
    previewChannel,
    stepThumbnailPreview,
    stepChannelPreview,
  ]);

  const previewUpload =
    thumbnailPreview && thumbnailPreview.uploads.length > 0
      ? thumbnailPreview.uploads[thumbnailPreview.index]
      : null;

  const recentUploadsExpansion = (r: ChannelCheckupReport) => {
    const uploads = keepPublicRecentUploads(r.recentUploads ?? []);
    return (
      <div style={{ padding: "0.75rem 1rem" }}>
        <div className="font-medium mb-2">
          Recent uploads ({uploads.length})
        </div>
        <div className="text-sm text-color-secondary mb-2">
          20 most recent public videos with a publish or scheduled date.
          Private and unlisted videos are not shown.
          Click a thumbnail to inspect; use arrows to browse.
        </div>
        {uploads.length === 0 ? (
          <div className="text-color-secondary text-sm">
            No recent uploads synced yet.
          </div>
        ) : (
          <DataTable value={uploads} size="small" dataKey="videoId">
            <Column
              header="Thumbnail"
              body={(u: RecentUpload, options) => (
                <button
                  type="button"
                  className="channel-checkup-thumb-button"
                  aria-label={`Inspect thumbnail for ${u.title}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    openThumbnailPreview(uploads, options.rowIndex);
                  }}
                  style={{
                    padding: 0,
                    border: "none",
                    background: "transparent",
                    cursor: "pointer",
                    display: "block",
                  }}
                >
                  <img
                    src={recentUploadThumbnailUrl(u.videoId)}
                    alt=""
                    style={{
                      width: RECENT_UPLOAD_THUMB_WIDTH,
                      height: RECENT_UPLOAD_THUMB_HEIGHT,
                      objectFit: "cover",
                      borderRadius: 4,
                      display: "block",
                    }}
                  />
                </button>
              )}
              style={{ width: `${RECENT_UPLOAD_THUMB_WIDTH + 24}px` }}
            />
            <Column
              header="Title"
              body={(u: RecentUpload) => (
                <a
                  href={u.videoUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ whiteSpace: "normal" }}
                >
                  {u.title}
                </a>
              )}
            />
            <Column
              header="Type"
              field="videoType"
              style={{ width: "120px" }}
            />
            <Column
              header="Status"
              body={(u: RecentUpload) => (
                <Tag
                  value={u.privacyStatus || "—"}
                  severity={privacyTagSeverity(u.privacyStatus)}
                />
              )}
              style={{ width: "110px" }}
            />
            <Column
              header="Uploaded"
              body={(u: RecentUpload) => (
                <RelativeDateCell iso={u.uploadedAt} />
              )}
              style={{ width: "140px" }}
            />
            <Column
              header="Published"
              body={(u: RecentUpload) => (
                <RelativeDateCell iso={u.publishedAt} />
              )}
              style={{ width: "140px" }}
            />
            <Column
              header="Tags"
              body={(u: RecentUpload) => {
                const tags = formatTags(u.tags);
                const shown = truncateText(tags === "—" ? null : tags);
                return (
                  <span title={tags === "—" ? undefined : tags}>{shown}</span>
                );
              }}
              style={{
                maxWidth: "220px",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            />
            <Column
              header="Description"
              body={(u: RecentUpload) => {
                const full = u.description?.trim() || "";
                return (
                  <span title={full || undefined}>
                    {truncateText(u.description)}
                  </span>
                );
              }}
              style={{
                maxWidth: "280px",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            />
          </DataTable>
        )}
      </div>
    );
  };

  const playlistsExpansion = (r: ChannelCheckupReport) => {
    const sortedPlaylists = [...r.playlists].sort((a, b) =>
      (b.lastItemAddedAt || "").localeCompare(a.lastItemAddedAt || "")
    );
    return (
      <div style={{ padding: "0.75rem 1rem" }}>
        <div className="font-medium mb-2">Playlists ({r.playlistCount})</div>
        {sortedPlaylists.length === 0 ? (
          <div className="text-color-secondary text-sm">
            No playlists synced yet.
          </div>
        ) : (
          <DataTable value={sortedPlaylists} size="small" dataKey="playlistId">
            <Column field="title" header="Playlist" />
            <Column
              header={
                <span>
                  Last Video Added to Playlist{" "}
                  <i
                    className="pi pi-info-circle text-color-secondary"
                    title={PLAYLIST_LAST_ADDED_TOOLTIP}
                  />
                </span>
              }
              body={(pl) => <RelativeDateCell iso={pl.lastItemAddedAt} />}
            />
            <Column
              header={
                <span>
                  Newest Video in Playlist{" "}
                  <i
                    className="pi pi-info-circle text-color-secondary"
                    title={PLAYLIST_NEWEST_VIDEO_TOOLTIP}
                  />
                </span>
              }
              body={(pl) => (
                <RelativeDateCell iso={pl.newestVideoPublishedAt} />
              )}
            />
          </DataTable>
        )}
      </div>
    );
  };

  const rowExpansionTemplate = (r: ChannelCheckupReport) => {
    if (expandedPanel === "uploads") return recentUploadsExpansion(r);
    if (expandedPanel === "playlists") return playlistsExpansion(r);
    return null;
  };

  const tableHeader = (
    <div className="flex flex-column gap-3">
      <div className="flex flex-wrap gap-3 align-items-end">
        <MultiSearch
          label="Channel ID(s)"
          selectedItems={selectedChannelIds}
          value={setSelectedChannelIds}
          placeholder="Paste or type channel IDs…"
          parseType="both"
        />
        <div className="flex flex-column gap-1">
          <label style={{ color: "grey", fontSize: "small" }}>
            Search channels
          </label>
          <AutoComplete
            value={channelSearchQuery}
            suggestions={channelSearchSuggestions}
            completeMethod={searchChannels}
            field="label"
            dropdown
            forceSelection={false}
            placeholder="Type a channel name…"
            onChange={(e) => {
              const v = e.value;
              if (typeof v === "string") {
                setChannelSearchQuery(v);
              } else if (v && typeof v === "object" && "label" in v) {
                setChannelSearchQuery((v as ChannelSearchOption).label);
              } else {
                setChannelSearchQuery("");
              }
            }}
            onSelect={(e) => {
              const opt = e.value as ChannelSearchOption;
              if (opt?.channelId) onChannelSearchSelect(opt);
            }}
            style={{ minWidth: "18rem" }}
            inputStyle={{ width: "100%" }}
          />
        </div>
        <div className="flex flex-column gap-1">
          <label style={{ color: "grey", fontSize: "small" }}>Type</label>
          <MultiSelect
            value={filters.channelType}
            options={typeOptions}
            onChange={(e) =>
              setFilters((prev) => ({ ...prev, channelType: e.value ?? [] }))
            }
            placeholder="Any type"
            display="chip"
            filter
            maxSelectedLabels={1}
            style={{ minWidth: "12rem" }}
          />
        </div>
        <div className="flex flex-column gap-1">
          <label style={{ color: "grey", fontSize: "small" }}>Brand</label>
          <MultiSelect
            value={filters.brand}
            options={brandOptions}
            onChange={(e) =>
              setFilters((prev) => ({ ...prev, brand: e.value ?? [] }))
            }
            placeholder="Any brand"
            display="chip"
            filter
            maxSelectedLabels={1}
            style={{ minWidth: "12rem" }}
          />
        </div>
        <div className="flex flex-column gap-1">
          <label style={{ color: "grey", fontSize: "small" }}>
            Banner state
          </label>
          <MultiSelect
            value={filters.bannerState}
            options={BANNER_STATE_OPTIONS}
            onChange={(e) =>
              setFilters((prev) => ({ ...prev, bannerState: e.value ?? [] }))
            }
            placeholder="Any state"
            display="chip"
            maxSelectedLabels={1}
            style={{ minWidth: "12rem" }}
          />
        </div>
        <div className="flex flex-column gap-1">
          <label style={{ color: "grey", fontSize: "small" }}>
            Channel manager
          </label>
          <MultiSelect
            value={filters.channelManagers}
            options={channelManagerOptions}
            onChange={(e) =>
              setFilters((prev) => ({
                ...prev,
                channelManagers: e.value ?? [],
              }))
            }
            placeholder="Any manager"
            display="chip"
            filter
            maxSelectedLabels={1}
            style={{ minWidth: "14rem" }}
          />
        </div>
        <div className="flex flex-column gap-1">
          <label style={{ color: "grey", fontSize: "small" }}>Columns</label>
          <MultiSelect
            value={visibleColumns}
            options={COLUMN_OPTIONS}
            onChange={(e) => setVisibleColumns(e.value ?? [])}
            optionLabel="label"
            optionValue="id"
            display="chip"
            placeholder="Show / hide columns"
            maxSelectedLabels={2}
            filter
            style={{ minWidth: "14rem" }}
          />
        </div>
        <div className="flex align-items-center gap-2" style={{ paddingBottom: "0.35rem" }}>
          <Checkbox
            inputId="showPausedInactive"
            checked={showPausedInactive}
            onChange={(e) => setShowPausedInactive(Boolean(e.checked))}
          />
          <label htmlFor="showPausedInactive" className="text-sm">
            Show paused / inactive
          </label>
        </div>
        <Button
          label="Reset"
          icon="pi pi-filter-slash"
          severity="secondary"
          outlined
          onClick={resetFilters}
        />
      </div>
      <span className="text-sm text-color-secondary">
        Showing {rows.length} of {allReports.length} channels
        {hiddenPausedCount > 0
          ? ` (${hiddenPausedCount} paused/inactive hidden)`
          : ""}
      </span>
      <SyncStatusBar status={syncStatus} />
    </div>
  );

  return (
    <PageContainer
      HomeMenuBarprops={{
        title: <span>Channel Checkup</span>,
      }}
    >
      <div
        style={{ width: "96%", margin: "0 auto", paddingTop: "1.5rem" }}
        className="channel-checkup-page"
      >
        {loading && allReports.length === 0 ? (
          <ProgressBar mode="indeterminate" style={{ height: "4px" }} />
        ) : (
          <DataTable
            value={rows}
            dataKey="channelId"
            header={tableHeader}
            paginator
            rows={25}
            rowsPerPageOptions={[25, 50, 100, 200]}
            removableSort
            className="p-datatable-sm channel-checkup-table"
            emptyMessage="No channels match the current filters."
            scrollable
            expandedRows={expandedRows}
            onRowToggle={(e) => {
              const data = e.data as ChannelCheckupReport[] | undefined;
              setExpandedRows(data?.length ? data : undefined);
              if (!data?.length) {
                setExpandedPanel(null);
              }
            }}
            rowExpansionTemplate={rowExpansionTemplate}
          >
            <Column
              expander={allowRowExpansion}
              className="channel-checkup-hidden-expander"
              style={{ width: 0, padding: 0 }}
            />
            <Column
              header="Channel"
              field="channelTitle"
              body={channelBody}
              sortable
              style={{ minWidth: "220px" }}
            />
            {isColumnVisible("banner") && (
              <Column
                header="Current Banner"
                field="bannerState"
                body={bannerBody}
                style={{
                  minWidth: "min(26vw, 440px)",
                  maxWidth: "min(34vw, 560px)",
                }}
              />
            )}
            {isColumnVisible("type") && (
              <Column
                header="Type"
                field="channelType"
                sortable
                style={{ minWidth: "160px" }}
              />
            )}
            {isColumnVisible("brand") && (
              <Column
                header="Brand"
                field="brand"
                body={brandBody}
                sortable
                style={{ minWidth: "180px" }}
                bodyStyle={{ whiteSpace: "normal" }}
              />
            )}
            {isColumnVisible("channelManagers") && (
              <Column
                header="Channel Manager"
                field="channelManagers"
                body={channelManagerBody}
                style={{ minWidth: "180px" }}
                bodyStyle={{ whiteSpace: "normal" }}
              />
            )}
            {isColumnVisible("featuredVideo") && (
              <Column
                header={featuredVideoHeader}
                field="featuredVideoDate"
                body={featuredVideoBody}
                sortable
                style={{ minWidth: "130px" }}
              />
            )}
            {isColumnVisible("scheduledVideo") && (
              <Column
                header={scheduledVideoHeader}
                field="maxScheduledVideo"
                body={scheduledVideoBody}
                sortable
                style={{ minWidth: "150px" }}
              />
            )}
            {isColumnVisible("scheduledShort") && (
              <Column
                header={scheduledShortHeader}
                field="maxScheduledShort"
                body={scheduledShortBody}
                sortable
                style={{ minWidth: "150px" }}
              />
            )}
            {isColumnVisible("lastPublished") && (
              <Column
                header="Last Published"
                field="lastPublishedAt"
                body={lastPublishedBody}
                sortable
                style={{ minWidth: "130px" }}
              />
            )}
            {isColumnVisible("lastUpload") && (
              <Column
                header={lastUploadHeader}
                field="lastUploadAt"
                body={lastUploadBody}
                sortable
                style={{ minWidth: "130px" }}
              />
            )}
            {isColumnVisible("playlists") && (
              <Column
                header="Playlists"
                field="playlistCount"
                body={playlistsBody}
                sortable
                style={{ minWidth: "90px" }}
              />
            )}
            {isColumnVisible("details") && (
              <Column
                header="Details"
                body={detailsBody}
                style={{ minWidth: "150px" }}
              />
            )}
            {isColumnVisible("qc") && (
              <Column
                header="QC"
                body={qcBody}
                style={{ minWidth: "110px" }}
              />
            )}
          </DataTable>
        )}
      </div>

      <Dialog
        header={
          previewUpload
            ? `${previewUpload.title} (${(thumbnailPreview?.index ?? 0) + 1} / ${
                thumbnailPreview?.uploads.length ?? 0
              })`
            : "Thumbnail preview"
        }
        visible={thumbnailPreview != null && previewUpload != null}
        onHide={() => setThumbnailPreview(null)}
        style={{ width: "min(1600px, 98vw)" }}
        contentStyle={{ maxHeight: "88vh", overflow: "auto" }}
        dismissableMask
        blockScroll
      >
        {previewUpload && thumbnailPreview && (
          <div className="channel-checkup-thumb-dialog">
            <div className="channel-checkup-thumb-dialog-top">
              <Button
                type="button"
                icon="pi pi-chevron-left"
                rounded
                text
                aria-label="Previous upload"
                onClick={() => stepThumbnailPreview(-1)}
                disabled={thumbnailPreview.uploads.length < 2}
              />
              <img
                className="channel-checkup-thumb-dialog-image"
                src={recentUploadPreviewThumbnailUrl(previewUpload.videoId)}
                alt=""
                onError={(e) => {
                  const img = e.currentTarget;
                  const hq = recentUploadThumbnailUrl(previewUpload.videoId);
                  if (img.src !== hq) img.src = hq;
                }}
              />
              <div className="channel-checkup-thumb-dialog-meta">
                <a
                  href={previewUpload.videoUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="channel-checkup-thumb-dialog-title"
                >
                  {previewUpload.title}
                </a>
                <DialogPanel label="Type">
                  <div>{previewUpload.videoType || "—"}</div>
                </DialogPanel>
                <DialogPanel label="Status">
                  <Tag
                    value={previewUpload.privacyStatus || "—"}
                    severity={privacyTagSeverity(previewUpload.privacyStatus)}
                  />
                </DialogPanel>
                <DialogPanel label="Uploaded">
                  <RelativeDateCell iso={previewUpload.uploadedAt} />
                </DialogPanel>
                <DialogPanel label="Published">
                  <RelativeDateCell iso={previewUpload.publishedAt} />
                </DialogPanel>
                <DialogPanel label="Video ID">
                  <div className="channel-checkup-thumb-dialog-id">
                    {previewUpload.videoId}
                  </div>
                </DialogPanel>
                <a
                  href={previewUpload.videoUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Open on YouTube
                </a>
              </div>
              <Button
                type="button"
                icon="pi pi-chevron-right"
                rounded
                text
                aria-label="Next upload"
                onClick={() => stepThumbnailPreview(1)}
                disabled={thumbnailPreview.uploads.length < 2}
              />
            </div>
            <div className="channel-checkup-thumb-dialog-bottom">
              <DialogPanel label="Description" variant="card">
                <div className="channel-checkup-thumb-dialog-description">
                  {previewUpload.description?.trim() || "—"}
                </div>
              </DialogPanel>
              <DialogPanel label="Tags" variant="ground">
                <DialogTagChips tags={previewUpload.tags} />
              </DialogPanel>
            </div>
          </div>
        )}
      </Dialog>

      <Dialog
        header={
          previewChannel
            ? `${previewChannel.channelTitle || previewChannel.channelId}${
                previewChannelIndex >= 0
                  ? ` (${previewChannelIndex + 1} / ${rows.length})`
                  : ""
              }`
            : "Channel details"
        }
        visible={previewChannel != null}
        onHide={() => setChannelPreviewId(null)}
        style={{ width: "min(1600px, 98vw)" }}
        contentStyle={{ maxHeight: "88vh", overflow: "auto" }}
        dismissableMask
        blockScroll
      >
        {previewChannel && (
          <div className="channel-checkup-channel-dialog">
            <div className="channel-checkup-channel-dialog-top">
              <Button
                type="button"
                icon="pi pi-chevron-left"
                rounded
                text
                aria-label="Previous channel"
                onClick={() => stepChannelPreview(-1)}
                disabled={rows.length < 2}
              />
              {previewChannel.bannerUrl ? (
                <img
                  className="channel-checkup-channel-dialog-image"
                  src={previewChannel.bannerUrl}
                  alt=""
                />
              ) : (
                <div className="channel-checkup-channel-dialog-image channel-checkup-channel-dialog-image-empty">
                  No banner
                </div>
              )}
              <div className="channel-checkup-channel-dialog-meta">
                <a
                  href={previewChannel.channelUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="channel-checkup-thumb-dialog-title"
                >
                  {previewChannel.channelTitle || previewChannel.channelId}
                </a>
                {isPausedOrInactive(previewChannel) && (
                  <Tag
                    value={
                      previewChannel.operationStatus ||
                      (previewChannel.inactive ? "Inactive" : "Paused")
                    }
                    severity="warning"
                  />
                )}
                <DialogPanel label="Channel ID">
                  <div className="channel-checkup-thumb-dialog-id">
                    {previewChannel.channelId}
                  </div>
                </DialogPanel>
                <DialogPanel label="Type">
                  <div>{previewChannel.channelType || "—"}</div>
                </DialogPanel>
                <DialogPanel label="Brand">
                  <div>{previewChannel.brand || "—"}</div>
                </DialogPanel>
                <DialogPanel label="Channel Manager">
                  <div>
                    {channelManagerLabels(previewChannel).length > 0
                      ? channelManagerLabels(previewChannel).join(", ")
                      : "—"}
                  </div>
                  {previewChannel.channelManagerEmails.length > 0 && (
                    <div className="text-sm text-color-secondary">
                      {previewChannel.channelManagerEmails.join(", ")}
                    </div>
                  )}
                </DialogPanel>
                <DialogPanel label="Banner state">
                  <div>{previewChannel.bannerState || "—"}</div>
                </DialogPanel>
                <DialogPanel label="Featured Video Published">
                  <div title={FEATURED_VIDEO_TOOLTIP}>
                    {formatDate(previewChannel.featuredVideoDate)}
                    <StaleValueMarker
                      syncedAt={previewChannel.featuredVideoSyncedAt}
                      trackingStarted={featuredTrackingStarted}
                    />
                  </div>
                </DialogPanel>
                <DialogPanel label="Farthest Scheduled Video">
                  <ScheduledDateCell
                    scheduled={previewChannel.maxScheduledVideo}
                    fallbackPublished={previewChannel.lastPublishedAt}
                  />
                </DialogPanel>
                <DialogPanel label="Farthest Scheduled Short">
                  <ScheduledDateCell
                    scheduled={previewChannel.maxScheduledShort}
                    fallbackPublished={previewChannel.lastPublishedAt}
                  />
                </DialogPanel>
                <DialogPanel label="Last Published">
                  <DateCell
                    iso={previewChannel.lastPublishedAt}
                    tracked={previewChannel.lastUpdatesAvailable}
                  />
                </DialogPanel>
                <DialogPanel label="Last Upload">
                  <DateCell
                    iso={previewChannel.lastUploadAt}
                    tracked={previewChannel.lastUpdatesAvailable}
                  />
                </DialogPanel>
                <DialogPanel label="Playlists">
                  <div>
                    {previewChannel.playlistCount > 0
                      ? previewChannel.playlistCount
                      : "—"}
                  </div>
                </DialogPanel>
                <div className="flex flex-wrap gap-3">
                  <a
                    href={previewChannel.channelUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Open on YouTube
                  </a>
                  <Button
                    label={`QC (${previewChannel.qcOpenIssueCount ?? 0})`}
                    icon="pi pi-external-link"
                    link
                    onClick={() => openTitleQc(previewChannel.channelId)}
                    style={{ padding: 0 }}
                  />
                </div>
              </div>
              <Button
                type="button"
                icon="pi pi-chevron-right"
                rounded
                text
                aria-label="Next channel"
                onClick={() => stepChannelPreview(1)}
                disabled={rows.length < 2}
              />
            </div>
          </div>
        )}
      </Dialog>
    </PageContainer>
  );
};

export default ChannelCheckup;
